import { Badge, Card, CardBody, CardHeader, CardFooter, Button, MultiSelect, Icon, Spinner, useToast } from "@aidkitorg/component-library";
import { DocumentLibraryItem } from "@aidkitorg/types/lib/convoai/documentLibrary.types";
import { safeParse } from "@aidkitorg/types/lib/util";
import { OpenAI } from "openai";
import { useCallback, useMemo, useState } from "react";
import { usePost } from "../../API";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";
import type { Model as MessageTemplate } from '@aidkitorg/aidkit/lib/convoai/schemas/MessageTemplate';
import { useAsyncInterval } from "../../Util";
import { useAsyncJobStatus } from "../../Hooks/AsyncJobStatusCheck";

const reasoning: OpenAI.ReasoningEffort[] = ['minimal', 'low', 'medium', 'high'];

const MODEL_SELECTION = {
  'gpt-5': { reasoning },
  'gpt-5-nano': { reasoning },
  'gpt-5-mini': { reasoning },
  'gpt-4.1': {},
  'gpt-4.1-mini': {},
  'gpt-4.1-nano': {}
};

export function MessageTemplateRunner() {

  const getAllMessageTemplates = usePost('/program/admin/prompts/scan');

  type RunConfig = {
    id: string,
    models: {
      name: OpenAI.ChatModel,
      params?: { reasoning?: NonNullable<OpenAI.ReasoningEffort> }
    }[],
    templates: MessageTemplate[],
    categories: string[],
  };

  const getAllDocuments = usePost('/ai/document_library/get_all');
  const [messageTemplates, setMessageTemplates] = useState<Record<string, MessageTemplate>>({});
  const [documentsByCategory, setDocumentsByCategory] = useState<Record<string, DocumentLibraryItem[]>>({});
  const [documentsById, setDocumentsById] = useState<Record<string, DocumentLibraryItem>>({});
  const [models] = useState<Record<string, { reasoning?: typeof reasoning }>>(MODEL_SELECTION);
  const [runConfigs, setRunConfigs] = useState<RunConfig[]>([]);

  const [selectedMessageTemplates, setSelectedMessageTemplates] = useState<{ label: string | JSX.Element, value: string }[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<{ label: string | JSX.Element, value: string }[]>([]);
  const [selectedModels, setSelectedModels] = useState<{ label: string | JSX.Element, value: string }[]>([]);
  useAsyncEffect(async () => {

    const templates = await getAllMessageTemplates({});
    setMessageTemplates(templates.items.reduce((acc, template) => {
      let candidate = acc[template.name];
      if (!candidate || template.version > (candidate.version ?? 0)) {
        candidate = template;
      }
      return {
        ...acc,
        [template.name]: candidate
      }
    }, {} as Record<string, MessageTemplate>));

    const docs = await getAllDocuments({ templates: 'omit' });
    const docsByCategory: typeof documentsByCategory = {};
    for (const doc of (docs.data ?? [])) {
      docsByCategory[doc.category] = (docsByCategory[doc.category] || []).concat(doc);
    }

    setDocumentsByCategory(docsByCategory);
    setDocumentsById(Object.fromEntries(docs.data?.map(doc => [doc.id, doc]) ?? []));
  }, []);

  const processResult = useCallback(({ result, documentId }: {
    template: {
      name: string,
      version?: number
    },
    documentId: string,
    model: {
      name: string,
      params?: {
        reasoning?: string
      }
    },
    result: string,
    conversationId: string
  }) => {
    const doc = documentsById[documentId];
    const docFields = Object.fromEntries((doc.metadata?.fields ?? []).map(({ name, value }) => [name, value]));
    const resultFields = safeParse(result, {});
    const fieldNames = new Set(Object.keys(docFields).concat(Object.keys(resultFields)));

    let passed = 0;
    let failed = 0;
    for (const field of Array.from(fieldNames)) {
      const actual = resultFields[field];
      const expected = docFields[field];

      if (actual === expected) {
        passed++;
      } else {
        failed++;
      }
    }


    return { passed, failed };
  }, [documentsById]);

  function RunConfiguration({ id, categories, templates, models }: RunConfig) {

    const runEvaluations = usePost('/ai/document_library/queue_evaluate');
    const { phase, setTaskId } = useAsyncJobStatus();
    const { toast } = useToast();

    const totalDocumentCount = useMemo(() =>
      categories.reduce((count, category) => count + documentsByCategory[category].length, 0)
    , [categories]
    );
    const messageTemplateCount = templates.length;
    const modelCount = models.length;

    const runEval = useCallback(async () => {

      const documentIds = categories
        .flatMap(category => documentsByCategory[category])
        .map(doc => doc.id);

      const evaluationResults = await runEvaluations({
        evaluation: {
          templates: templates.map(template => ({
            models,
            name: template.name,
            version: template.version,
          })),
          documentIds
        }
      }).catch((error) => ({ data: null, error }));

      if (!evaluationResults.data || evaluationResults.error) {
        toast({ description: 'failed to initiate evaluations', variant: 'error' });
        return;
      }

      setTaskId(evaluationResults.data.taskId);

    }, [documentsByCategory, runEvaluations, processResult]);

    const statusIcon = useMemo(() => {
      let iconName: Parameters<typeof Icon>[0]['name'] = "CheckCircleIcon";
      let iconColor: string = 'green';
      switch (phase) {
        case 'FAILED':
          iconName = "XMarkIcon";
          iconColor = 'red';
          break;
        case 'FINISHED':
          iconName = "CheckCircleIcon";
          break;
        default:
          return <></>;
      }

      return <Icon
        name={iconName}
        color={iconColor}
        className="w-8 h-8"
      />
    }, [phase]);

    return <Card className="space-y-2">
      <CardHeader
        title={<span className="flex items-center space-x-1">
          {statusIcon}
          <span>{phase === 'STARTED' ? 'Running' : 'Run'}</span>
          <span><strong>{messageTemplateCount}</strong> Prompts</span>
          <span>Across <strong>{modelCount}</strong> Models</span>
          <span>Against <strong>{totalDocumentCount}</strong> Docs,</span>
          <span>Totaling <strong>{modelCount * messageTemplateCount * totalDocumentCount}</strong> Evaluations</span>
        </span>}
        actionButton={
          <div className="space-x-2">
            <Button
              isDisabled={phase === 'STARTED' || phase === 'QUEUED'}
              icon={<Icon name="PlayIcon" />}
              onClick={runEval}
            />
            <Button
              variant="secondary"
              isDisabled={phase === 'STARTED' || phase === 'QUEUED'}
              icon={<Icon name="XMarkIcon" />}
              onClick={() => {
                setRunConfigs(runConfigs.filter(config => config.id !== id));
              }}
            />
          </div>}
      />
      <CardBody className="mt-1 space-y-2">
        <div className="space-x-2">
          {templates.map(template =>
            <Badge key={template.name} variant="info">{template.name} v{template.version ?? 0}</Badge>
          )}
        </div>
        <div className="p-1 space-x-2 border border-gray-200">
          Models: {models.map(m => {
            let name = m.name;
            if(m.params?.reasoning) {
              name += '-' + m.params.reasoning;
            }
            return name
          }).join(', ')}
        </div>
        <div className="space-y-2">
          {categories.map(category => {

            const documentCount = documentsByCategory[category].length;
            const variations = Array.from(new Set(
              documentsByCategory[category].flatMap(doc => doc.metadata?.variations ?? [])
            ));

            return <Card key={category} className="space-x-1" expandable>
              <CardHeader title={`${category} (${documentCount})`} />
              <CardBody className="space-x-2">
                {variations.length ? (<>
                  <span className="font-semibold">Variations:</span>
                  <span>{variations.join(', ')}</span>
                </>)
                  : (<span>No Variations</span>)
                }
              </CardBody>
            </Card>
          })}
        </div>
      </CardBody>
      <CardFooter>
        <div>
          <div hidden={phase === 'FINISHED' || phase === 'NOT_SUBMITTED' || phase === 'FAILED'}><Spinner /></div>
        </div>
      </CardFooter>
    </Card>
  }

  const runConfigInstances = useMemo(() =>
    runConfigs.map((config, i) => <RunConfiguration key={i} {...config} />)
  , [runConfigs]);

  return <div className="space-y-4">
    <Card>
      <CardHeader title="New Run" />
      <CardBody className="py-2">
        <MultiSelect
          label="Message Templates"
          searchable
          filterFunction={(opt, search) => {
            const { name } = messageTemplates[opt.value];
            return name.includes(search);
          }}
          description="Select message templates you want to analyze documents with"
          value={selectedMessageTemplates}
          onChange={setSelectedMessageTemplates}
          options={Object.entries(messageTemplates).map(([name]) => ({
            label: <span>{name}</span>,
            value: name
          }))}
        />
        <MultiSelect
          label="Document Categories"
          description="Select documents want to analyze with message templates"
          searchable
          value={selectedCategories}
          onChange={setSelectedCategories}
          options={Object.keys(documentsByCategory).map(category => ({
            label: category,
            value: category
          }))}
        />
        <MultiSelect
          label="LLMs"
          description="Select the LLMs you want to run with"
          searchable
          value={selectedModels}
          onChange={setSelectedModels}
          options={Object.entries(models).flatMap(([model, { reasoning }]) =>
            reasoning ? reasoning.map(effort => ({
              label: `${model}-${effort}`,
              value: `${model}|${effort}`
            })) : [{
              label: model,
              value: model
            }])}
        />
      </CardBody>
      <CardFooter>
        <Button onClick={() => {
          setRunConfigs(runConfigs.concat({
            id: runConfigs.length.toString(),
            models: selectedModels.map(model => {
              const [modelName, reasoning] = model.value.split('|');
              return {
                name: modelName as OpenAI.ChatModel,
                params: reasoning ? {
                  reasoning: reasoning as NonNullable<OpenAI.ReasoningEffort>
                } : undefined
              }
            }),
            templates: selectedMessageTemplates.map(template => messageTemplates[template.value]),
            categories: selectedCategories.map(category => category.value)
          }));
        }}>Add</Button>
      </CardFooter>
    </Card>
    <div className="space-y-2">
      <h4>Run Configurations</h4>
      <ul className="space-y-2 max-h-[75dvh] overflow-scroll">
        {runConfigInstances}
      </ul>
    </div>
  </div>;
}
